import React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import * as api from '../../api';

import Loader from '../../components/Loader';
import NavBar, { View } from '../../components/NavBar';
import { Language } from '../../reducers/store';
import SpinnerButton from '../../components/SpinnerButton';

import styles from './index.module.css';

import { useTheme } from '../../hooks/common/useTheme';

enum ResubStatus {
  None = 'NONE',
  Loading = 'LOADING',
  Done = 'DONE',
}

interface StateProps {
  theme: api.PracticeTheme.ThemeResponse | null;
}

interface DispatchProps {
  setTheme: (theme: api.PracticeTheme.ThemeResponse) => void;
}

interface Props extends StateProps, DispatchProps {}

const Unsubscribe: React.FC<Props> = ({ theme, setTheme }) => {
  const { t } = useTranslation();
  const { unsubscribeCode } = useParams<{
    unsubscribeCode: string;
    lang: Language;
  }>();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [resubscribeStatus, setResubscribeStatus] = useState<ResubStatus>(
    ResubStatus.None,
  );
  const { isThemeLoading } = useTheme();

  useEffect(() => {
    async function unsubscribeWithCode() {
      try {
        await api.client.Previsit.unsubscribeEmail(unsubscribeCode);
      } catch {
        setErrorMessage('unableToProcessRequest');
      } finally {
        setLoading(false);
      }
    }

    unsubscribeWithCode();
  }, [unsubscribeCode, setLoading]);

  const handleSubscribe = async () => {
    setResubscribeStatus(ResubStatus.Loading);
    try {
      await api.client.Previsit.subscribeEmail(unsubscribeCode);
    } catch (err) {
      setErrorMessage('unableToResubscribe');
      setResubscribeStatus(ResubStatus.None);
      return;
    }
    setResubscribeStatus(ResubStatus.Done);
  };

  if (loading || isThemeLoading) return <Loader />;
  return (
    <>
      <NavBar view={View.Unsubscribe} />
      <div className={styles.Container}>
        {errorMessage ? (
          <p>{t(errorMessage)}</p>
        ) : (
          <>
            <p>{t('unsubscribedMessage')}</p>
            <p>{t('resubscribePrompt')}</p>
            <div className={styles.ResubscribeContainer}>
              <SpinnerButton
                disabled={resubscribeStatus !== ResubStatus.None}
                showSpinner={resubscribeStatus === ResubStatus.Loading}
                onClick={handleSubscribe}
              >
                {resubscribeStatus === ResubStatus.Done
                  ? t('resubscribed')
                  : t('resubscribe')}
              </SpinnerButton>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Unsubscribe;
